<template lang="html">
  <v-content>
    <v-dialog
      v-model="reviewAndExportForEntityDialogOpened"
      @keydown.esc="reviewAndExportForEntityDialogOpened = false"
      persistent
      fullscreen
    >
      <v-card class="content-container review-export-dialog-content">
        <v-card-title>
          <v-layout align-center v-if="selectedEntity">
            <span class="title mr-4"
              >{{ selectedEntity.tax_return_name }} for {{ selectedYear }}</span
            >
            <sx-button-group :is-joined="true">
              <div @click="reviewAndExportTab = 0">
                <sx-button
                  type="secondary"
                  :is-left="true"
                  :is-active="reviewAndExportTab === 0"
                >
                  Review
                </sx-button>
              </div>
              <div @click="reviewAndExportTab = 1">
                <sx-button
                  type="secondary"
                  :is-right="true"
                  :is-active="reviewAndExportTab === 1"
                >
                  Export
                </sx-button>
              </div>
            </sx-button-group>
            <v-spacer />
            <sc-button-group>
              <sc-button
                @click="triggerRefreshInReviewAndExportForEntity()"
                type="ghost"
                icon-left="refresh"
                :is-icon-only="true"
                :with-bottom-tooltip="true"
                >Refresh</sc-button
              >
              <sc-button
                @click="downloadLeadsheet(selectedEntity)"
                type="primary"
                icon-left="download"
                :with-tooltip="true"
                >Leadsheet</sc-button
              >
              <v-btn
                small
                fab
                flat
                class="white error--text text-capitalize ma-0"
                @click="reviewAndExportForEntityDialogOpened = false"
              >
                <v-icon size="20"> clear </v-icon>
              </v-btn>
            </sc-button-group>
          </v-layout>
        </v-card-title>
        <ReviewAndExportForEntity
          :selected-entity="selectedEntity"
          :selected-year="selectedYear"
          :show-leadsheet.sync="showLeadsheet"
          :review-and-export-tab.sync="reviewAndExportTab"
          :zoom="zoom"
          ref="reviewAndExportForEntity"
          @refresh="$emit('refresh')"
          biggerView
        />
      </v-card>
    </v-dialog>

    <v-data-table
      :headers="entitiesWithTaxFormsHeaders"
      :items="mappedEntitiesWithTaxForms"
      :loading="tableLoading"
      :pagination.sync="passedPagination"
      :rows-per-page-items="passedPagination.options"
      :select-all="$vuetify.breakpoint.mdAndUp"
      :total-items="totalItems"
      class="text-xs-left"
    >
      <template slot="items" slot-scope="props">
        <tr :key="props.index" slot="header">
          <td class="hidden-sm-and-down">
            <v-checkbox
              v-model="props.selected"
              primary
              hide-details
            ></v-checkbox>
          </td>
          <td class="text-xs-left">
            <v-layout
              align-center
              @click="
                changeCurrentCompany(
                  $event,
                  props.item.id,
                  `/review-export/${props.item.hash_id}`,
                )
              "
              class="primary--text"
              style="cursor: pointer"
            >
              {{ props.item.tax_return_name }}
              <v-chip
                class="ml-2 caption"
                v-if="props.item.tax_return_id"
                small
                >{{ props.item.tax_return_id }}</v-chip
              >
            </v-layout>
          </td>
          <td class="text-xs-left">
            <v-chip
              class="error white--text"
              small
              v-if="props.item.action_required_documents.length"
              @click="openEntityDocumentsDialog(props.item)"
            >
              <v-icon class="white--text mr-1">warning</v-icon>
              {{ props.item.action_required_documents.length }}/{{
                props.item.tax_forms.length
              }}
              Review Required
            </v-chip>
            <span v-else class="darkgrey--text">None</span>
          </td>
          <td class="text-xs-left">
            <span v-if="props.item.scheduled_documents.length"
              >{{ props.item.scheduled_documents.length }} Scheduled</span
            >
            <span
              v-if="
                props.item.scheduled_documents.length &&
                  props.item.tax_forms.length -
                    props.item.action_required_documents.length >
                    props.item.scheduled_documents.length
              "
              >/</span
            >
            <span v-if="props.item.completed_documents.length"
              >{{ props.item.completed_documents.length }} Completed</span
            >
            <span v-if="isEmpty(props.item.tax_forms)" class="darkgrey--text"
              >Not Applicable</span
            >
          </td>
          <td class="text-xs-center">
            <v-layout align-center justify-center>
              <v-tooltip bottom>
                <v-btn
                  icon
                  flat
                  color="primary"
                  class="mx-0"
                  @click="openEntityDocumentsDialog(props.item)"
                  slot="activator"
                >
                  <v-icon class="material-icons-outlined document-action__icon"
                    >visibility</v-icon
                  >
                </v-btn>
                <span>View Details</span>
              </v-tooltip>
              <v-tooltip bottom>
                <v-btn
                  icon
                  flat
                  color="primary"
                  class="mx-0"
                  @click="downloadLeadsheet(props.item)"
                  slot="activator"
                >
                  <v-icon class="material-icons-outlined document-action__icon"
                    >download</v-icon
                  >
                </v-btn>
                <span>Download Leadsheet</span>
              </v-tooltip>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-content>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import ReviewAndExportForEntity from '@/components/ReviewAndExportForEntity';
import DataEntryMixin from '@/mixins/DataEntryMixin';
import AdminFunctionsMixin from '@/mixins/AdminFunctionsMixin';
import UtilsMixins from '@/mixins/UtilsMixins';
import { yearOptions } from '@/constants/variables';
import SxFlex from '@/components/sx/SxFlex';
import SxIcon from '@/components/sx/SxIcon';
import SxSmall from '@/components/sx/SxSmall';
import SxButton from '@/components/sx/SxButton';
import SxButtonGroup from '@/components/sx/SxButtonGroup';

export default {
  name: 'ReviewAndExportTable',
  components: {
    ReviewAndExportForEntity,
    SxFlex,
    SxIcon,
    SxSmall,
    SxButton,
    SxButtonGroup,
  },
  mixins: [DataEntryMixin, AdminFunctionsMixin, UtilsMixins],
  props: {
    tableLoading: {
      type: Boolean,
    },
    entitiesWithTaxForms: {
      type: Array,
      default: [],
    },
    selectedYear: {
      type: Number,
      default: 2024,
    },
    pagination: {
      type: Object,
    },
    totalItems: {
      type: Number,
    },
  },
  data(instance) {
    return {
      yearOptions,
      zoom: false,
      entitiesWithTaxFormsHeaders: [
        {
          text: 'Entity',
          align: 'left',
          sortable: true,
          value: 'tax_return_name',
          width: '30%',
        },
        {
          text: 'Documents to review',
          align: 'left',
          sortable: true,
          value: 'action_required_documents_count',
          width: '25%',
        },
        {
          text: 'Export Status',
          align: 'left',
          width: '35%',
        },
        {
          text: 'Actions',
          align: 'center',
          width: '5%',
        },
      ],
      loading: false,
      selectedFirms: [],
      selectedFirm: 'All',
      showLeadsheet: true,
      selectAll: false,
      exportTasks: [],
      selectedEntity: null,
      firmList: [],
      connectedTaxSoftwares: [],
      selectedFirmId: null,
      timer: null,
      reviewAndExportForEntityDialogOpened: false,
      selectedEntityType: 'All',
      relatedList: null,
      active: 0,
      reviewAndExportTab: 0,
      passedPagination: instance.pagination,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
    mappedEntitiesWithTaxForms() {
      return this.entitiesWithTaxForms.map(e => {
        const action_required_documents = e.tax_forms.filter(
          d => !d.reviewer_id,
        );
        return {
          action_required_documents,
          action_required_documents_count: action_required_documents.length,
          reviewed_documents: e.tax_forms.filter(d => d.reviewer_id),
          scheduled_documents: e.tax_forms.filter(
            d => d.export_status === 'scheduled',
          ),
          in_progress_documents: e.tax_forms.filter(d =>
            ['in_progress', 'in_qa', 'system_failure'].includes(
              d.export_status,
            ),
          ),
          scheduled_with_progress_documents: e.tax_forms.filter(d =>
            ['scheduled', 'in_progress', 'in_qa', 'system_failure'].includes(
              d.export_status,
            ),
          ),
          completed_documents: e.tax_forms.filter(
            d => d.export_status === 'completed',
          ),
          ...e,
        };
      });
    },
  },
  methods: {
    isEmpty,
    moment,
    // actionRequiredPercentage(entity) {
    //   return (
    //     (entity.action_required_documents.length / entity.tax_forms.length) *
    //     100
    //   );
    // },
    // reviewedPercentage(entity) {
    //   return (entity.reviewed_documents.length / entity.tax_forms.length) * 100;
    // },
    // scheduledPercentage(entity) {
    //   return (
    //     (entity.scheduled_documents.length / entity.tax_forms.length) * 100
    //   );
    // },
    // completedPercentage(entity) {
    //   return (
    //     (entity.completed_documents.length / entity.tax_forms.length) * 100
    //   );
    // },
    openEntityDocumentsDialog(entity) {
      this.selectedEntity = entity;
      this.showLeadsheet = true;
      this.reviewAndExportTab = 0;
      this.reviewAndExportForEntityDialogOpened = true;
    },
    getStatus(status) {
      return this.exportTaskStatuses.find(s => s.value === status);
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    },

    async getAllEntitiesWithTaxFormsWithPolling() {
      let refreshInterval;

      const getAllEntitiesWithTaxForms = async () => {
        try {
          this.refreshing = true;
          this.$emit('refresh');
          if (
            isEmpty(
              this.entitiesWithTaxForms.some(
                e =>
                  !isEmpty(
                    e.tax_forms.filter(t => t.export_status === 'processing'),
                  ),
              ),
            )
          ) {
            clearInterval(refreshInterval);
            this.refreshing = false;
          }
        } catch (error) {
          console.log(error);
        }
      };
      refreshInterval = setInterval(getAllEntitiesWithTaxForms, 10000);
    },
    triggerRefreshInReviewAndExportForEntity() {
      var reviewAndExportForEntity = this.$refs.reviewAndExportForEntity;
      reviewAndExportForEntity.getAllExportableDocumentsForEntity();
    },
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  async created() {
    this.$emit('refresh');
    if (
      !isEmpty(
        this.entitiesWithTaxForms.some(
          e =>
            !isEmpty(e.tax_forms.filter(t => t.export_status === 'processing')),
        ),
      )
    ) {
      this.getAllEntitiesWithTaxFormsWithPolling();
    }
    // await this.getAllExportTasks();
  },
  watch: {
    selectedYear() {
      if (!this.refreshing) {
        this.getAllEntitiesWithTaxFormsWithPolling();
      } else {
        this.$emit('refresh');
      }
    },
    reviewAndExportTab: {
      handler(tab) {
        if (tab === 0) {
          this.showLeadsheet = true;
        } else {
          this.showLeadsheet = false;
        }
      },
    },
    passedPagination: {
      handler(pagination) {
        this.$emit('update:pagination', pagination);
      },
      deep: true,
    },
  },
};
</script>

<style lang="css">
.review-export-dialog-content {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.user-panel {
  border: 1px solid #bdbdbd;
}
.notification__text {
  color: #616161;
  font-size: 14px;
}
.notification__time {
  color: #757575;
}
.users-overview-data-table tbody td:nth-child(2),
.users-overview-data-table tbody td:nth-child(5) {
  padding-left: 5px;
  padding-right: 5px;
}
.users-overview-data-table tbody td:nth-child(7) {
  padding-left: 0px;
  padding-right: 5px;
}
.settings__container {
  max-width: 1100px;
}
.bordered {
  border: 1px solid #bdbdbd;
}
</style>
